import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 TrophyIcon,
 CheckCircleIcon,
 XCircleIcon,
 ArrowDownTrayIcon,
 ClockIcon,
 CreditCardIcon,
 AcademicCapIcon,
 ExclamationTriangleIcon,
 ArrowPathIcon
} from '@heroicons/react/24/outline';
import API_BASE_URL from '../Config/config';
import { hasFullAccess } from '../utils/paymentStatus';

// Local storage utility functions (same as IndividualRoles)
const slugify = (s ="") => {
 if (s === null || s === undefined) return"";
 return s.toString().trim().toLowerCase()
 .replace(/&/g," and")
 .replace(/\+/g,"")
 .replace(/[^a-z0-9]+/g,"-")
 .replace(/(^-|-$)+/g,"");
};

const pKey = (userId, tag) => `progress_${userId}_${slugify(tag)}`;
const readProgress = (userId, tag) => {
 try {
 const raw = localStorage.getItem(pKey(userId, tag));
 if (!raw) return { completedTaskIds: [], reviewStateById: {} };
 const parsed = JSON.parse(raw);
 if (Array.isArray(parsed)) return { completedTaskIds: parsed, reviewStateById: {} };
 return { completedTaskIds: parsed.completedTaskIds || [], reviewStateById: parsed.reviewStateById || {} };
 } catch { return { completedTaskIds: [], reviewStateById: {} }; }
};

const CertificatePage = () => {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [eligibility, setEligibility] = useState(null);
 const [certificate, setCertificate] = useState(null);
 const [error, setError] = useState(null);
 const [generating, setGenerating] = useState(false);
 
 // Local progress data
 const [localProgress, setLocalProgress] = useState(null);

 const userId = localStorage.getItem('user_id') || 
 (() => {
 try {
 const user = JSON.parse(localStorage.getItem('user') || '{}');
 return user.userId || user.user_id || '';
 } catch {
 return '';
 }
 })();

 const loadLocalProgress = useCallback(() => {
 try {
 console.log('🔍 Loading local progress for userId:', userId);
 
 // Get user enrollment data from localStorage - check multiple possible keys
 let enrollment = localStorage.getItem(`enrollment_${userId}`);
 console.log('📋 Enrollment data (primary key):', enrollment);
 
 // If not found, check for alternative key formats
 if (!enrollment) {
 const enrollmentKeys = Object.keys(localStorage).filter(key => 
 key.includes('enrollment') && key.includes(userId)
 );
 console.log('🔍 Found enrollment keys:', enrollmentKeys);
 
 if (enrollmentKeys.length > 0) {
 enrollment = localStorage.getItem(enrollmentKeys[0]);
 console.log('📋 Enrollment data (alternative key):', enrollment);
 }
 }
 
 if (enrollment) {
 const enrollmentData = JSON.parse(enrollment);
 console.log('📋 Parsed enrollment data:', enrollmentData);
 
 // Read progress from localStorage
 const courseName = enrollmentData.bunchName || enrollmentData.technology ||"";
 console.log('📚 Course name:', courseName);
 
 // If no course name, try to find any progress data for this user
 if (!courseName || courseName.trim() ==="") {
 console.log('⚠️ No course name found, searching for any user progress data');
 const progressKeys = Object.keys(localStorage).filter(key => key.includes('progress') && key.includes(userId));
 console.log('🔍 User progress keys found:', progressKeys);
 
 if (progressKeys.length > 0) {
 const rawProgress = localStorage.getItem(progressKeys[0]);
 console.log('📊 Raw progress from first key:', rawProgress);
 try {
 const parsedProgress = JSON.parse(rawProgress);
 console.log('📊 Parsed progress:', parsedProgress);
 setLocalProgress(parsedProgress);
 return;
 } catch (err) {
 console.warn('❌ Error parsing progress:', err);
 }
 }
 
 // Set empty progress if no data found
 setLocalProgress({ completedTaskIds: [], reviewStateById: {} });
 return;
 }
 
 // Debug: Show what progress key would be generated
 const expectedProgressKey = pKey(userId, courseName);
 console.log('🔑 Expected progress key:', expectedProgressKey);
 
 // Debug: Check what progress keys exist in localStorage
 const progressKeys = Object.keys(localStorage).filter(key => key.includes('progress'));
 console.log('🔍 All progress keys in localStorage:', progressKeys);
 
 // Debug: Check if expected key exists
 const expectedKeyExists = progressKeys.includes(expectedProgressKey);
 console.log('✅ Expected key exists:', expectedKeyExists);
 
 const progress = readProgress(userId, courseName);
 console.log('📊 Progress data:', progress);
 
 // If no progress found, try to find any progress data for this user
 if (progress.completedTaskIds.length === 0) {
 const userProgressKeys = progressKeys.filter(key => key.includes(userId));
 console.log('🔍 User-specific progress keys:', userProgressKeys);
 
 if (userProgressKeys.length > 0) {
 const rawProgress = localStorage.getItem(userProgressKeys[0]);
 console.log('📊 Raw progress data from key:', userProgressKeys[0], rawProgress);
 try {
 const parsedProgress = JSON.parse(rawProgress);
 console.log('📊 Parsed progress data:', parsedProgress);
 
 // Update progress with found data
 setLocalProgress(parsedProgress);
 return;
 } catch (err) {
 console.warn('❌ Error parsing progress data:', err);
 }
 }
 }
 
 setLocalProgress(progress);
 
 console.log('✅ Local progress loaded successfully:', {
 userId,
 course: courseName,
 completedTasks: progress.completedTaskIds.length,
 progress
 });
 } else {
 console.log('⚠️ No enrollment data found in localStorage');
 // Set empty progress to trigger timeout
 setLocalProgress({ completedTaskIds: [], reviewStateById: {} });
 }
 } catch (err) {
 console.warn('❌ Could not load local progress:', err);
 // Set empty progress to trigger timeout
 setLocalProgress({ completedTaskIds: [], reviewStateById: {} });
 }
 }, [userId]);

 const calculateLocalProgress = useCallback(() => {
 if (!localProgress) return null;
 
 // Get total tasks count from localStorage (stored by dashboard)
 let totalTasks = 50; // Default fallback
 
 try {
 // Try to get actual course data from localStorage
 const enrollmentData = localStorage.getItem('enrollment');
 if (enrollmentData) {
 const enrollment = JSON.parse(enrollmentData);
 console.log('📚 Found enrollment data:', enrollment);
 
 // Try to get total tasks count stored by dashboard
 if (enrollment.courseName || enrollment.bunchName) {
 const courseName = enrollment.courseName || enrollment.bunchName;
 const totalTasksKey = `totalTasks_${courseName}_${enrollment.experienceLevel}`;
 const storedTotalTasks = localStorage.getItem(totalTasksKey);
 
 if (storedTotalTasks) {
 totalTasks = parseInt(storedTotalTasks);
 console.log(`📊 Found stored task count: ${totalTasks} for course: ${courseName}`);
 } else {
 console.log(`📊 No stored task count found, using default: ${totalTasks}`);
 }
 }
 }
 } catch (enrollmentError) {
 console.warn('Could not parse enrollment data, using default totalTasks:', enrollmentError);
 }
 
 const completedTasks = localProgress.completedTaskIds.length;
 const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
 
 console.log('📈 Calculated local progress:', {
 totalTasks,
 completedTasks,
 progressPercentage,
 localProgress
 });
 
 return {
 progressPercentage,
 completedTasks,
 totalTasks,
 isComplete: progressPercentage >= 100
 };
 }, [localProgress]);

 const checkEligibility = useCallback(async () => {
 try {
 // Check payment status first
 let paymentComplete = false;
 try {
 if (userId) {
 paymentComplete = await hasFullAccess(userId);
 console.log('💳 Payment status check result:', paymentComplete);
 }
 } catch (paymentErr) {
 console.warn('⚠️ Failed to check payment status:', paymentErr);
 // Continue with false, but log the error
 }
 
 // First try to get local progress
 const localProgressData = calculateLocalProgress();
 
 if (localProgressData) {
 console.log('✅ Using local progress data:', localProgressData);
 
 // Create eligibility object using local data
 const isEligible = localProgressData.isComplete && paymentComplete;
 
 const localEligibility = {
 isEligible: isEligible,
 progressPercentage: localProgressData.progressPercentage,
 hasActiveSubscription: paymentComplete,
 subscription: null,
 requirements: {
 progressComplete: localProgressData.isComplete,
 paymentComplete: paymentComplete
 },
 message: isEligible
 ?"All requirements completed! You can generate your certificate."
 : !localProgressData.isComplete && !paymentComplete
 ? `Complete requirements: ${localProgressData.progressPercentage}% progress, Payment required`
 : !localProgressData.isComplete
 ? `Complete requirements: ${localProgressData.progressPercentage}% progress remaining`
 :"Progress complete! Payment required for certificate."
 };
 
 setEligibility(localEligibility);
 setLoading(false);
 return;
 }
 
 // Fallback to backend API if local data not available
 const response = await fetch(`${API_BASE_URL}/api/certificate/eligibility/${userId}`);
 const data = await response.json();
 
 if (data.success) {
 // Merge payment status with backend data
 const eligibilityData = {
 ...data.eligibility,
 hasActiveSubscription: paymentComplete,
 requirements: {
 ...data.eligibility.requirements,
 paymentComplete: paymentComplete
 }
 };
 setEligibility(eligibilityData);
 } else {
 // Even if backend fails, use payment status
 setEligibility({
 isEligible: false,
 progressPercentage: 0,
 hasActiveSubscription: paymentComplete,
 subscription: null,
 requirements: {
 progressComplete: false,
 paymentComplete: paymentComplete
 },
 message: paymentComplete
 ?"Complete requirements: 0% progress remaining"
 :"Complete requirements: 0% progress, Payment required"
 });
 }
 } catch (err) {
 setError('Failed to check eligibility');
 console.error('Error checking eligibility:', err);
 } finally {
 setLoading(false);
 }
 }, [calculateLocalProgress, userId]);

 useEffect(() => {
 if (!userId) {
 setError('User not logged in');
 setLoading(false);
 return;
 }

 // Load data immediately
 loadLocalProgress();
 
 // Fallback timeout to prevent infinite loading
 const fallbackTimeout = setTimeout(async () => {
 console.log('⏰ Fallback timeout reached, checking payment status');
 
 // Check payment status before showing default state
 let paymentComplete = false;
 try {
 if (userId) {
 paymentComplete = await hasFullAccess(userId);
 }
 } catch (err) {
 console.warn('⚠️ Payment check failed in timeout:', err);
 }
 
 setLoading(false);
 setEligibility({
 isEligible: false,
 progressPercentage: 0,
 hasActiveSubscription: paymentComplete,
 subscription: null,
 requirements: {
 progressComplete: false,
 paymentComplete: paymentComplete
 },
 message: paymentComplete
 ?"Complete requirements: 0% progress remaining"
 :"Complete requirements: 0% progress, Payment required"
 });
 }, 5000); // 5 second fallback timeout

 return () => clearTimeout(fallbackTimeout);
 }, [userId, loadLocalProgress]);

 // Check eligibility when localProgress changes
 useEffect(() => {
 if (localProgress) {
 checkEligibility();
 } else {
 // If no local progress after a timeout, show default state
 const timeout = setTimeout(async () => {
 console.log('⏰ No local progress found, checking payment status');
 
 // Check payment status before showing default state
 let paymentComplete = false;
 try {
 if (userId) {
 paymentComplete = await hasFullAccess(userId);
 }
 } catch (err) {
 console.warn('⚠️ Payment check failed in timeout:', err);
 }
 
 setLoading(false);
 setEligibility({
 isEligible: false,
 progressPercentage: 0,
 hasActiveSubscription: paymentComplete,
 subscription: null,
 requirements: {
 progressComplete: false,
 paymentComplete: paymentComplete
 },
 message: paymentComplete
 ?"Complete requirements: 0% progress remaining"
 :"Complete requirements: 0% progress, Payment required"
 });
 }, 2000); // 2 second timeout

 return () => clearTimeout(timeout);
 }
 }, [localProgress, checkEligibility]);

  // Real-time progress monitoring - listen for updates
  useEffect(() => {
    const handleUpdate = () => {
      console.log('📈 Progress updated event received! Refreshing certificate page...');
      loadLocalProgress();
    };

    window.addEventListener('skillgenProgressUpdated', handleUpdate);
    return () => window.removeEventListener('skillgenProgressUpdated', handleUpdate);
  }, [loadLocalProgress]);


 const generateCertificate = async () => {
 if (!eligibility?.isEligible) {
 setError('You are not eligible for certificate generation');
 return;
 }

 setGenerating(true);
 try {
 const response = await fetch(`${API_BASE_URL}/api/certificate/generate/${userId}`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 });
 
 const data = await response.json();
 
 if (data.success) {
 setCertificate(data.certificate);
 setError(null);
 } else {
 setError(data.error || 'Failed to generate certificate');
 }
 } catch (err) {
 setError('Failed to generate certificate');
 console.error('Error generating certificate:', err);
 } finally {
 setGenerating(false);
 }
 };

 const downloadCertificate = () => {
 if (certificate?.downloadUrl) {
 window.open(`${API_BASE_URL}${certificate.downloadUrl}`, '_blank');
 }
 };

 const refreshProgress = async () => {
 console.log('🔄 Manual progress refresh triggered');
 
 // Debug: Show all localStorage data
 console.log('🔍 All localStorage keys:', Object.keys(localStorage));
 console.log('🔍 All localStorage data:', Object.fromEntries(
 Object.keys(localStorage).map(key => [key, localStorage.getItem(key)])
 ));
 
 setLoading(true);
 loadLocalProgress();
 
 // Also refresh eligibility to get updated payment status
 await checkEligibility();
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center">
 <div className="text-center">
 <ArrowPathIcon className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
 <div className="text-primary">Loading certificate information...</div>
 </div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center p-6">
 <div className="max-w-md w-full rounded-xl border border-red-500/40 bg-[#0b0b0b] p-6">
 <div className="text-red-400 font-semibold mb-2 flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5" />
 Error
 </div>
 <div className="text-sm text-muted mb-4">{error}</div>
 <button
 onClick={() => navigate('/dashboard')}
 className="w-full bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary transition-colors"
 >
 Back to Dashboard
 </button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-bgMain text-body">
 {/* Header */}
 <header className="sticky top-0 z-10 border-b border-accentGray/30 bg-bgHeaderFooter/90 backdrop-blur">
 <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
 <div>
 <h1 className="text-xl md:text-2xl font-extrabold text-primary tracking-tight flex items-center gap-2">
 <TrophyIcon className="h-6 w-6 text-primary" />
 Certificate Center
 </h1>
 <p className="text-sm text-muted">
 Certificate Management
 </p>
 </div>
 <button
 onClick={() => navigate('/dashboard')}
 className="bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary transition-colors"
 >
 Back to Dashboard
 </button>
 </div>
 </header>

 <main className="mx-auto max-w-7xl px-4 py-6 space-y-6">

 {/* Eligibility Status */}
 {eligibility && (
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6">
 <div className="flex items-center justify-between mb-4">
 <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
 {eligibility.isEligible ? (
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 ) : (
 <XCircleIcon className="h-5 w-5 text-red-400" />
 )}
 Certificate Eligibility
 </h2>
 <button
 onClick={refreshProgress}
 className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
 title="Refresh progress data"
 >
 <ArrowPathIcon className="h-4 w-4" />
 Refresh
 </button>
 </div>
 
 <div className={`p-4 rounded-lg mb-4 ${
 eligibility.isEligible 
 ? 'bg-green-500/10 border border-green-500/30' 
 : 'bg-red-500/10 border border-red-500/30'
 }`}>
 <div className={`font-semibold mb-2 ${
 eligibility.isEligible ? 'text-green-400' : 'text-red-400'
 }`}>
 {eligibility.isEligible ? '✅ Eligible for Certificate' : '❌ Not Eligible'}
 </div>
 <p className="text-sm text-muted">{eligibility.message}</p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-3">
 <div className="flex items-center gap-3">
 <div className={`w-3 h-3 rounded-full ${
 eligibility.requirements.progressComplete ? 'bg-green-400' : 'bg-red-400'
 }`}></div>
 <div className="flex items-center gap-3">
 <span className="text-sm text-muted">
 Progress: {eligibility.progressPercentage}%
 </span>
 <div className="flex-1 bg-gray-700 rounded-full h-2 max-w-32">
 <div 
 className="bg-primary h-2 rounded-full transition-all duration-500"
 style={{ width: `${Math.min(eligibility.progressPercentage, 100)}%` }}
 ></div>
 </div>
 </div>
 </div>
 <div className="flex items-center gap-3">
 <div className={`w-3 h-3 rounded-full ${
 eligibility.requirements.paymentComplete ? 'bg-green-400' : 'bg-red-400'
 }`}></div>
 <span className="text-sm text-muted">
 Payment: {eligibility.requirements.paymentComplete ? 'Completed' : 'Required'}
 </span>
 </div>
 </div>
 <div className="space-y-3">
 <div className="flex items-center gap-2 text-sm text-muted">
 <ClockIcon className="h-4 w-4" />
 <span>Progress tracking active</span>
 </div>
 <div className="flex items-center gap-2 text-sm text-muted">
 <CreditCardIcon className="h-4 w-4" />
 <span>Payment verification enabled</span>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Certificate Generation */}
 {eligibility?.isEligible && (
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6">
 <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <AcademicCapIcon className="h-5 w-5" />
 Generate Certificate
 </h2>
 
 {!certificate ? (
 <div className="space-y-4">
 <p className="text-sm text-muted">
 You have completed all requirements! Click the button below to generate your certificate.
 </p>
 <button
 onClick={generateCertificate}
 disabled={generating}
 className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary disabled:opacity-50 transition-colors flex items-center gap-2"
 >
 {generating ? (
 <>
 <ArrowPathIcon className="h-5 w-5 animate-spin" />
 Generating Certificate...
 </>
 ) : (
 <>
 <TrophyIcon className="h-5 w-5" />
 Generate Certificate
 </>
 )}
 </button>
 </div>
 ) : (
 <div className="space-y-4">
 <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
 <div className="text-green-400 font-semibold mb-2 flex items-center gap-2">
 <CheckCircleIcon className="h-5 w-5" />
 Certificate Generated Successfully!
 </div>
 <p className="text-sm text-muted">
 Your certificate has been generated and is ready for download.
 </p>
 </div>
 
 <div className="flex gap-4">
 <button
 onClick={downloadCertificate}
 className="bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center gap-2"
 >
 <ArrowDownTrayIcon className="h-5 w-5" />
 Download Certificate
 </button>
 
 <button
 onClick={generateCertificate}
 disabled={generating}
 className="bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center gap-2"
 >
 <ArrowPathIcon className="h-5 w-5" />
 Regenerate
 </button>
 </div>
 </div>
 )}
 </div>
 )}

 {/* Requirements for Non-Eligible Users */}
 {eligibility && !eligibility.isEligible && (
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6">
 <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5" />
 Complete Requirements
 </h2>
 
 <div className="space-y-4">
 <p className="text-sm text-muted">
 To generate your certificate, you need to complete the following requirements:
 </p>
 
 <div className="space-y-3">
 <div className="flex items-center gap-3 p-3 bg-bgSection rounded-lg">
 <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
 eligibility.requirements.progressComplete ? 'bg-green-400' : 'bg-red-400'
 }`}>
 {eligibility.requirements.progressComplete && (
 <CheckCircleIcon className="h-3 w-3 text-black" />
 )}
 </div>
 <div>
 <div className="text-white font-medium">Complete All Course Tasks</div>
 <div className="text-sm text-muted">
 Current progress: {eligibility.progressPercentage}%
 </div>
 </div>
 </div>
 
 <div className="flex items-center gap-3 p-3 bg-bgSection rounded-lg">
 <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
 eligibility.requirements.paymentComplete ? 'bg-green-400' : 'bg-red-400'
 }`}>
 {eligibility.requirements.paymentComplete && (
 <CheckCircleIcon className="h-3 w-3 text-black" />
 )}
 </div>
 <div>
 <div className="text-white font-medium">Complete Payment</div>
 <div className="text-sm text-muted">
 {eligibility.requirements.paymentComplete 
 ? 'Payment completed successfully' 
 : 'Payment required to access certificate'
 }
 </div>
 </div>
 </div>
 </div>
 
 {!eligibility.requirements.paymentComplete && (
 <button
 onClick={() => navigate('/payment')}
 className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary transition-colors flex items-center gap-2"
 >
 <CreditCardIcon className="h-5 w-5" />
 Complete Payment
 </button>
 )}
 </div>
 </div>
 )}

 {/* Error Display */}
 {error && (
 <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6">
 <div className="text-red-400 font-semibold mb-2 flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5" />
 Error
 </div>
 <p className="text-sm text-muted">{error}</p>
 </div>
 )}
 </main>
 </div>
 );
};

export default CertificatePage;




